import type { CSSProperties } from "react";
import type { RowData } from "@tanstack/react-table";
import { resolveColumnMeta } from "../columns/columnKinds";
import type { DataGridHeader, DataGridPin } from "../columns/types";
import type { GridController } from "../focus/gridController";
import { getDisplayColumns } from "../focus/navigation";
import {
    columnAfterVar,
    columnStartVar,
    getSpanStyle,
} from "../layout/columnVars";
import { getPinEdge } from "../layout/pinning";
import { RefetchBar } from "../states/RefetchBar";
import type { DataGridAtoms, DataGridReactTable } from "../types";
import { HeaderCell } from "./HeaderCell";
import gridStyles from "./Grid.module.scss";
import styles from "./Header.module.scss";

interface HeaderProps<TData extends RowData> {
    table: DataGridReactTable<TData>;
    controller: GridController;
    isRefetching: boolean;
    columnVariants: DataGridAtoms["columnVariants"];
}

const groupStyle = <TData extends RowData>(
    header: DataGridHeader<TData>,
    pin: DataGridPin | false,
): CSSProperties => {
    const leafIds = header.getLeafHeaders().map((leaf) => leaf.column.id);
    const style = getSpanStyle(leafIds);
    if (pin === "start" && leafIds[0]) {
        style.insetInlineStart = `var(${columnStartVar(leafIds[0])})`;
    }
    const lastLeafId = leafIds[leafIds.length - 1];
    if (pin === "end" && lastLeafId) {
        style.insetInlineEnd = `var(${columnAfterVar(lastLeafId)})`;
    }
    return style;
};

/**
 * Sticky header rowgroup. Start, center, and end header groups render
 * separately, so a group header never spans a pinned boundary.
 */
export const Header = <TData extends RowData>({
    table,
    controller,
    isRefetching,
    columnVariants,
}: Readonly<HeaderProps<TData>>) => {
    const sections: ReadonlyArray<{
        pin: DataGridPin | false;
        groups: ReturnType<typeof table.getCenterHeaderGroups>;
    }> = [
        { pin: "start", groups: table.getStartHeaderGroups() },
        { pin: false, groups: table.getCenterHeaderGroups() },
        { pin: "end", groups: table.getEndHeaderGroups() },
    ];
    const rowCount = Math.max(...sections.map(({ groups }) => groups.length));
    const colIndexById = new Map(
        getDisplayColumns(table).map((column, index) => [column.id, index + 1]),
    );
    const { sorting } = table.state;
    const isMultiSort = sorting.length > 1;

    const renderHeader = (
        header: DataGridHeader<TData>,
        pin: DataGridPin | false,
    ) => {
        if (header.subHeaders.length > 0 || header.isPlaceholder) {
            const label = header.isPlaceholder
                ? null
                : resolveColumnMeta(header.column.columnDef, header.column.id)
                      .headerLabel;
            return (
                <div
                    key={header.id}
                    role="columnheader"
                    aria-colindex={colIndexById.get(
                        header.getLeafHeaders()[0]?.column.id ?? "",
                    )}
                    aria-colspan={header.colSpan}
                    className={styles.groupHeaderCell}
                    style={groupStyle(header, pin)}
                    data-pin={pin || undefined}
                >
                    {label}
                </div>
            );
        }
        const sortDirection = header.column.getIsSorted();
        return (
            <HeaderCell
                key={header.id}
                header={header}
                controller={controller}
                pin={pin}
                pinEdge={getPinEdge(header.column)}
                ariaColIndex={colIndexById.get(header.column.id) ?? 0}
                sortDirection={sortDirection}
                nextSortOrder={header.column.getNextSortingOrder()}
                sortIndex={sortDirection ? header.column.getSortIndex() : -1}
                isMultiSort={isMultiSort}
                columnVariants={columnVariants}
            />
        );
    };

    return (
        <div role="rowgroup" className={styles.header} data-dt-header="">
            {Array.from({ length: rowCount }, (_, rowIndex) => (
                <div
                    key={rowIndex}
                    role="row"
                    aria-rowindex={rowIndex + 1}
                    className={styles.headerRow}
                >
                    {sections.map(({ pin, groups }) =>
                        pin === "end"
                            ? null
                            : groups[rowIndex]?.headers.map((header) =>
                                  renderHeader(header, pin),
                              ),
                    )}
                    <div className={gridStyles.filler} aria-hidden />
                    {sections[2].groups[rowIndex]?.headers.map((header) =>
                        renderHeader(header, "end"),
                    )}
                </div>
            ))}
            {isRefetching ? <RefetchBar /> : null}
        </div>
    );
};
